import React from 'react';
import {Link} from 'react-router-dom';
import {motion} from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const {
  FiArrowLeft,
  FiArrowUpRight,
  FiCheckCircle,
  FiCreditCard,
  FiLock,
  FiMessageCircle,
  FiSmartphone,
  FiTag,
} = FiIcons;

const SIGNUP_URL = 'https://linkacode.com/signup';
const LINKACODE_URL = 'https://linkacode.com';
const VIZI_APP_URL = 'https://viziplayer.com/app';
const TELEGRAM_LINK = 'https://t.me/sc4apps';

const products = [
  {
    name: 'Single code',
    price: '£30 / year',
    details: 'One assigned code, for example KQ481203.',
  },
  {
    name: 'Custom code',
    price: '£50 / year',
    details: 'Choose 6–20 letters and numbers, for example LENDING.',
  },
  {
    name: 'Group of 5',
    price: '£80 / year',
    details: 'Five consecutive codes, for example KQ481203 to KQ481207.',
  },
  {
    name: 'Custom group of 5',
    price: '£100 / year',
    details: 'Five codes you choose, sharing their first four characters.',
  },
];

const prerequisites = [
  'A server address: the host and, if required, its port. Use neither a full URL nor an M3U link.',
  'Permission to share the server address: it must be your server or one you are authorised to use.',
  'An Xtream Codes username and password for each person. Vizi codes support Xtream Codes logins only.',
  'Vizi Player installed on the iPhone or iPad that will use the code, and a bank card for the purchase.',
];

const createSteps = [
  [
    'Create an account',
    'Visit LinkACode and sign up with your email and a password of at least 10 characters. If you already have an account, sign in.',
  ],
  [
    'Choose a code',
    'Pick the product that fits your needs from the options below. Each code lasts one year and does not renew automatically.',
  ],
  [
    'Pay by card',
    'Complete the purchase by card. The payment statement will show OMWSD LTD, not LinkACode.',
  ],
  [
    'Set the server address',
    'Enter the server host and optional port. Leave out http:// and any path. Check it carefully: you can change it freely for the first 24 hours; after that, it is fixed.',
  ],
  [
    'Share the code',
    'Send people the code or its share card, which includes the code and a QR code.',
  ],
];

const useSteps = [
  ['Open Vizi Player', 'On each device, open Vizi Player and choose Add Playlist.'],
  [
    'Enter or scan the code',
    'Type the code in the Playlist URL or LinkACode.com ID box, or scan the QR code on the share card.',
  ],
  [
    'Sign in to the server',
    'Enter that server’s Xtream Codes username and password, just as you would with its full address. The credentials go to the server, not LinkACode.',
  ],
  [
    'Connect',
    'Vizi looks up the code, connects to the server, and loads the playlist. The server address is not shown on screen, saved with the playlist, or included in error messages.',
  ],
];

function StepList({title, label, steps, icon}) {
  return (
    <section className="rounded-3xl border border-white/10 bg-[#111113] p-6 md:p-8">
      <div className="mb-7 flex items-center gap-3">
        <div className="flex h-11 w-11 items-center justify-center rounded-2xl bg-yellow-500 text-black">
          <SafeIcon icon={icon} className="text-xl" />
        </div>
        <div>
          <p className="text-xs font-black uppercase tracking-[0.2em] text-yellow-500">{label}</p>
          <h2 className="text-2xl font-black text-white">{title}</h2>
        </div>
      </div>
      <ol className="space-y-5">
        {steps.map(([heading, description], index) => (
          <li key={heading} className="flex gap-4">
            <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full border border-yellow-500/40 text-sm font-black text-yellow-400">
              {index + 1}
            </span>
            <div>
              <h3 className="mb-1 font-bold text-white">{heading}</h3>
              <p className="text-sm leading-relaxed text-slate-400">{description}</p>
            </div>
          </li>
        ))}
      </ol>
    </section>
  );
}

function AdvertGuide() {
  return (
    <main className="px-6 pb-24 pt-32">
      <div className="mx-auto max-w-5xl">
        <Link to="/" className="mb-10 inline-flex items-center gap-2 text-sm font-semibold text-slate-400 transition-colors hover:text-yellow-500">
          <SafeIcon icon={FiArrowLeft} /> Back to portfolio
        </Link>

        <motion.header
          initial={{opacity: 0, y: 18}}
          animate={{opacity: 1, y: 0}}
          className="mb-10"
        >
          <div className="mb-5 inline-flex items-center gap-2 rounded-full border border-yellow-500/30 bg-yellow-500/10 px-4 py-2 text-xs font-bold uppercase tracking-[0.18em] text-yellow-400">
            <SafeIcon icon={FiTag} /> LinkACode to Vizi
          </div>
          <h1 className="mb-4 text-4xl font-black tracking-tight text-white md:text-6xl">
            Setup <span className="text-yellow-500">guide</span>
          </h1>
          <p className="max-w-3xl text-lg leading-relaxed text-slate-400">
            The server owner creates a code at LinkACode and shares it. Each person enters that code in Vizi, then signs in with their own server username and password. Setup takes about five minutes.
          </p>
        </motion.header>

        <div className="mb-8 grid gap-6 md:grid-cols-[1fr_1.5fr]">
          <div className="rounded-3xl border border-yellow-500/25 bg-gradient-to-br from-yellow-500/15 to-[#151511] p-7">
            <p className="mb-3 text-xs font-black uppercase tracking-[0.2em] text-yellow-400">Offer code</p>
            <p className="mb-3 text-3xl font-black tracking-wider text-white">SC4APPS30</p>
            <p className="text-sm leading-relaxed text-slate-300">The advert shows a 30% offer. Check the offer terms when purchasing.</p>
          </div>
          <div className="rounded-3xl border border-white/10 bg-[#151517] p-7">
            <h2 className="mb-4 text-xl font-black text-white">Before you start</h2>
            <ul className="space-y-3">
              {prerequisites.map((item) => (
                <li key={item} className="flex gap-3 text-sm leading-relaxed text-slate-400">
                  <SafeIcon icon={FiCheckCircle} className="mt-0.5 shrink-0 text-yellow-500" />
                  {item}
                </li>
              ))}
            </ul>
          </div>
        </div>

        <section className="mb-8 overflow-hidden rounded-3xl border border-white/10 bg-[#111113]">
          <div className="border-b border-white/10 px-6 py-5">
            <div className="flex items-center gap-3">
              <SafeIcon icon={FiCreditCard} className="text-xl text-yellow-500" />
              <h2 className="text-xl font-black text-white">Choose a one-year code</h2>
            </div>
            <p className="mt-2 text-sm text-slate-400">Codes do not renew automatically.</p>
          </div>
          <div className="grid gap-px bg-white/10 sm:grid-cols-2">
            {products.map((product) => (
              <div key={product.name} className="bg-[#111113] p-5">
                <p className="font-bold text-white">{product.name}</p>
                <p className="my-1 text-lg font-black text-yellow-400">{product.price}</p>
                <p className="text-sm leading-relaxed text-slate-400">{product.details}</p>
              </div>
            ))}
          </div>
        </section>

        <div className="mb-8 grid gap-6 lg:grid-cols-2">
          <StepList title="Create and share your code" label="Once · server owner" steps={createSteps} icon={FiTag} />
          <StepList title="Add it in Vizi Player" label="Each person · each device" steps={useSteps} icon={FiSmartphone} />
        </div>

        <section className="mb-8 rounded-3xl border border-white/10 bg-[#111113] p-6 md:p-8">
          <div className="mb-6 flex items-center gap-3">
            <SafeIcon icon={FiLock} className="text-xl text-yellow-500" />
            <h2 className="text-2xl font-black text-white">Managing your code</h2>
          </div>
          <div className="grid gap-5 md:grid-cols-2">
            <div>
              <h3 className="mb-1 font-bold text-white">Switching it on or off</h3>
              <p className="text-sm leading-relaxed text-slate-400">Manage your code from your LinkACode account. Vizi checks the code when the playlist refreshes—about once a day by default, or when the person refreshes—so a change applies at the next check, not instantly.</p>
            </div>
            <div>
              <h3 className="mb-1 font-bold text-white">Changing the server address</h3>
              <p className="text-sm leading-relaxed text-slate-400">The address can be changed freely for the first 24 hours after issue, then it is fixed. LinkACode support may reopen it if there was a genuine mistake.</p>
            </div>
            <div>
              <h3 className="mb-1 font-bold text-white">Expiry and groups</h3>
              <p className="text-sm leading-relaxed text-slate-400">Codes last one year and do not auto-renew. A lapsed code stops working as if it had been switched off. Group-of-5 codes only work alongside codes from the same group.</p>
            </div>
            <div>
              <h3 className="mb-1 font-bold text-white">Privacy</h3>
              <p className="text-sm leading-relaxed text-slate-400">Server addresses are stored encrypted with AES-256. LinkACode keeps a daily total per code, not a record of who used it or when.</p>
            </div>
          </div>
          <p className="mt-6 border-t border-white/10 pt-5 text-sm leading-relaxed text-slate-400">
            One code can be used by as many people and on as many devices as you like. Each person still needs their own server username and password.
          </p>
        </section>

        <div className="flex flex-wrap gap-3">
          <a href={SIGNUP_URL} target="_blank" rel="noopener noreferrer" className="inline-flex items-center justify-center gap-2 rounded-xl bg-yellow-500 px-5 py-3 font-black text-black transition-colors hover:bg-yellow-400">
            Create a LinkACode account <SafeIcon icon={FiArrowUpRight} />
          </a>
          <a href={VIZI_APP_URL} target="_blank" rel="noopener noreferrer" className="inline-flex items-center justify-center gap-2 rounded-xl bg-white px-5 py-3 font-black text-black transition-colors hover:bg-slate-200">
            Get Vizi Player <SafeIcon icon={FiArrowUpRight} />
          </a>
          <a href={LINKACODE_URL} target="_blank" rel="noopener noreferrer" className="inline-flex items-center justify-center gap-2 rounded-xl border border-white/15 px-5 py-3 font-bold text-white transition-colors hover:border-yellow-500/50">
            Visit LinkACode <SafeIcon icon={FiArrowUpRight} />
          </a>
          <a href={TELEGRAM_LINK} target="_blank" rel="noopener noreferrer" className="inline-flex items-center justify-center gap-2 rounded-xl border border-white/15 px-5 py-3 font-bold text-white transition-colors hover:border-yellow-500/50">
            Ask SC4APPS <SafeIcon icon={FiMessageCircle} />
          </a>
        </div>
      </div>
    </main>
  );
}

export default AdvertGuide;